import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Outlet, ScrollRestoration } from "react-router-dom";
import { LuWifiOff } from "react-icons/lu";
import { UpgradeDialog } from "./UpgradeDialog";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";
import { RELOGIN_EVENT, UPGRADE_EVENT, showError } from "../lib/feedback";
import { t } from "../i18n";

// Envolve todas as rotas: o que vale para o app inteiro (sem internet,
// sessão expirada, limite do plano, erro que ninguém tratou) fica aqui.

const subscribeOnline = (cb: () => void) => {
  window.addEventListener("online", cb);
  window.addEventListener("offline", cb);
  return () => {
    window.removeEventListener("online", cb);
    window.removeEventListener("offline", cb);
  };
};

const OfflineBanner: React.FC = () => {
  const online = useSyncExternalStore(subscribeOnline, () => navigator.onLine);
  if (online) return null;
  return (
    <div role="status" className="sticky top-0 z-50 flex items-center justify-center gap-2 bg-warning-soft px-4 py-2 text-caption text-fg">
      <LuWifiOff aria-hidden />
      {t("offline.banner")}
    </div>
  );
};

export const RootLayout: React.FC = () => {
  const { relogin } = useAuthenticatedFetch();
  const [upgradeMessage, setUpgradeMessage] = useState<string | null>(null);

  useEffect(() => {
    const onUpgrade = (e: Event) => setUpgradeMessage((e as CustomEvent<string>).detail);
    const onRejection = (e: PromiseRejectionEvent) => showError(e.reason);
    // Só exceções de verdade: erros de recurso (img/script) não têm `error`.
    const onError = (e: ErrorEvent) => { if (e.error instanceof Error) showError(e.error); };

    window.addEventListener(RELOGIN_EVENT, relogin);
    window.addEventListener(UPGRADE_EVENT, onUpgrade);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onError);
    return () => {
      window.removeEventListener(RELOGIN_EVENT, relogin);
      window.removeEventListener(UPGRADE_EVENT, onUpgrade);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onError);
    };
  }, [relogin]);

  return (
    <>
      <OfflineBanner />
      <Outlet />
      {/* "Voltar" volta para onde a tela estava; telas novas abrem no topo. */}
      <ScrollRestoration />
      {upgradeMessage && <UpgradeDialog message={upgradeMessage} onClose={() => setUpgradeMessage(null)} />}
    </>
  );
};
